import * as React from "react";
import { AccountHolder } from "../../Account";
import {
  Alert,
  Box,
  Paper,
  SelectChangeEvent,
  Snackbar,
  TablePagination,
} from "@mui/material";
import { useLanguage } from "../../Language";
import { SavedSearchTypeKey } from "../../SavedSearchTypeToggle";
import { LoaderFunctionArgs, useLoaderData } from "react-router-dom";
import axios from "axios";
import { LoaderData } from "./MyAccount";
import {
  DynamiResourceListView,
  SavedSearchesService,
} from "./searches/SavedSearches";
import {
  DeleteListReturn,
  ListType,
  ResourceListSortType,
} from "./ListService";
import { DeleteListDialog } from "./lists/DeleteListDialog";
import { RenameListDialog } from "./lists/RenameListDialog";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { SavedSearchesMobileGrid } from "./SavedSearchesMobileGrid";
import { SavedSearchesDesktopGrid } from "./SavedSearchesDesktopGrid";
import { SavedSearchesToolbar } from "./SavedSearchesToolbar";
import { useTranslateFacetName } from "../search/translateFacetName";
import { isReadingLevelSearch } from "../search/advanced/AdvancedSearchTerm";
import { readingLevelSearchAdapter } from "../search/advanced/reading-levels/readingLevelSearchAdapter";

interface ListCountReturn {
  count: number;
}

export function getSearchLink({
  expression,
  facetFilters,
  branchFilters,
  sortType,
  advanced,
  readingLevelFilters,
  url,
}: DynamiResourceListView) {
  const params = new URLSearchParams();
  params.set("term", expression);
  if (facetFilters?.length) {
    params.set("facetFilters", JSON.stringify(facetFilters));
  }
  if (branchFilters?.length) {
    params.set("branchFilters", JSON.stringify(branchFilters));
  }
  if (sortType) {
    params.set("sort", sortType);
  }
  // TODO extend DynamiResourceListView to include audience

  if (advanced) {
    const rawTerm = JSON.parse(expression);
    if (rawTerm.isReadingLevel) {
      readingLevelSearchAdapter.setTerm(params, {
        ...rawTerm,
        readingLevelFilters,
      });
      return `/advanced-search/reading-levels/results?${params.toString()}`;
    } else {
      return `/advanced-search/boolean/results?${params.toString()}`;
    }
  }

  return `/search/ls?${params.toString()}`;
}

export async function savedSearchesLoader({ params }: LoaderFunctionArgs) {
  const defaultPageSize: number = 10;
  const defaultPage: number = 0;
  const defaultSortType: ResourceListSortType =
    ResourceListSortType.CreationDate;
  const personalCount: number = (
    await axios.get<ListCountReturn>(`list/count/dynamic`)
  ).data.count;
  await AccountHolder.fetchAccountData(); // necessary for AccountHolder.isConfigAdmin
  const configCount: number = AccountHolder.isConfigAdmin
    ? (await axios.get<ListCountReturn>(`list/count/dynamic/config`)).data.count
    : null;
  const defaultTab: SavedSearchTypeKey = AccountHolder.isConfigAdmin
    ? "configListType"
    : "personalListType";
  const searches = await SavedSearchesService.getSearches(
    AccountHolder.isConfigAdmin,
    defaultPage,
    defaultPageSize,
    defaultSortType
  );

  return {
    searches,
    defaultPageSize,
    defaultPage,
    defaultSortType,
    defaultTab,
    personalCount,
    configCount,
  };
}

export const SavedSearchesPage: React.FC = () => {
  const {
    configCount: initialConfigCount,
    personalCount: initialPersonalCount,
    searches,
    defaultPage,
    defaultPageSize,
    defaultSortType,
    defaultTab,
  } = useLoaderData() as LoaderData<typeof savedSearchesLoader>;
  const { translate } = useLanguage();
  const isMobile = useIsMobile();
  const [personalCount, setPersonalCount] =
    React.useState<number>(initialPersonalCount);
  const [configCount, setConfigCount] =
    React.useState<number>(initialConfigCount);

  const [listType, setListType] =
    React.useState<SavedSearchTypeKey>(defaultTab);

  const [displayedSearches, setDisplayedSearches] =
    React.useState<DynamiResourceListView[]>(searches);

  const [batchDeleteError, setBatchDeleteError] =
    React.useState<boolean>(false);

  const isConfig = React.useMemo<boolean>(
    () => listType === "configListType",
    [listType]
  );
  const total = React.useMemo<number>(
    () => (isConfig ? configCount : personalCount),
    [isConfig, configCount, personalCount]
  );

  const [sortType, setSortType] =
    React.useState<ResourceListSortType>(defaultSortType);
  const [page, setPage] = React.useState<number>(defaultPage);
  const [pageSize, setPageSize] = React.useState<number>(defaultPageSize);

  const [listToDelete, setListToDelete] =
    React.useState<DynamiResourceListView | null>(null);

  const [listToRename, setListToRename] =
    React.useState<DynamiResourceListView | null>(null);
  const [selected, setSelected] = React.useState<DynamiResourceListView[]>([]);
  const selectedRows = React.useMemo<DynamiResourceListView["id"][]>(
    () => selected.map((row) => row.id),
    [selected]
  );

  const translateFacetName = useTranslateFacetName();

  const handleSelectAllClick = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const allRows = await await SavedSearchesService.getSearches(
        isConfig,
        0,
        total,
        sortType
      );
      setSelected(allRows);
      return;
    }
    setSelected([]);
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    row: DynamiResourceListView
  ) => {
    const { id } = row;
    const selectedIndex = selectedRows.indexOf(id);

    if (selectedIndex === -1) {
      setSelected([...selected, row]);
    } else {
      setSelected(selected.filter((row) => row.id !== id));
    }
  };

  const changeHandler = async (
    event: React.MouseEvent<HTMLElement>,
    newValue: SavedSearchTypeKey
  ) => {
    setListType(newValue);
    setPage(0);
    const searches = await SavedSearchesService.getSearches(
      newValue === "configListType", // isConfig won't have settled at this point
      0,
      pageSize,
      sortType
    );
    setDisplayedSearches(searches);
  };

  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
    const searches = await SavedSearchesService.getSearches(
      isConfig,
      newPage,
      pageSize,
      sortType
    );
    setDisplayedSearches(searches);
  };
  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const size = parseInt(event.target.value, 10);
    setPageSize(size);
    setPage(0);
    const searches = await SavedSearchesService.getSearches(
      isConfig,
      0,
      size,
      sortType
    );
    setDisplayedSearches(searches);
  };

  const handleSortChange = async function (event: SelectChangeEvent) {
    const newSort = event.target.value as ResourceListSortType;
    setSortType(newSort);
    setPage(0);
    const searches = await SavedSearchesService.getSearches(
      isConfig,
      0,
      pageSize,
      newSort
    );
    setDisplayedSearches(searches);
  };

  const onClickDelete = async (event: React.MouseEvent) => {
    try {
      const { data } = await axios.post<DeleteListReturn[]>(`lists/delete`, {
        listIds: selectedRows,
      });

      AccountHolder.fetchAccountSummaryAndPatron(true); // update account menu list count
      const deleted = data
        .map(({ success }, index) => (success ? selectedRows[index] : null))
        .filter((row) => row);
      const successCount = deleted.length;

      if (successCount > 0) {
        SuccessMessageControl.show(
          `${successCount} ${translate("removedFromSavedSearches")}`
        );

        if (listType === "configListType") {
          setConfigCount(configCount - successCount);
        } else {
          setPersonalCount(personalCount - successCount);
        }

        // setSelected(selected.filter((row) => deleted.indexOf(row.id) === -1));
        setSelected([]);
        const newCount = total - successCount;
        const pageStart = page * pageSize;
        const newPage =
          newCount <= 0
            ? 0
            : newCount <= pageStart
            ? Math.ceil(newCount / pageSize) - 1
            : page;
        handleChangePage(newPage);
      } else {
        const errorCount = data.filter((value) => !value.success).length;
        if (errorCount > 0) {
          setBatchDeleteError(true);
        }
      }
    } catch (e) {
      setBatchDeleteError(true);
    }
  };

  return (
    <Box>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <SavedSearchesToolbar
          displayedSearches={displayedSearches}
          selected={selected}
          total={total}
          listType={listType}
          personalCount={personalCount}
          configCount={configCount}
          changeHandler={changeHandler}
          sortType={sortType}
          handleSelectAllClick={handleSelectAllClick}
          handleSortChange={handleSortChange}
          onClickDelete={onClickDelete}
        />
        {isMobile ? (
          <SavedSearchesMobileGrid
            displayedSearches={displayedSearches}
            selectedRows={selectedRows}
            handleClick={handleClick}
            setListToRename={setListToRename}
            setListToDelete={setListToDelete}
            translateFacetName={translateFacetName}
          />
        ) : (
          <SavedSearchesDesktopGrid
            selected={selected}
            displayedSearches={displayedSearches}
            selectedRows={selectedRows}
            total={total}
            handleSelectAllClick={handleSelectAllClick}
            handleClick={handleClick}
            setListToRename={setListToRename}
            setListToDelete={setListToDelete}
            translateFacetName={translateFacetName}
          />
        )}

        {total > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            labelRowsPerPage={translate("size")}
            page={page}
            onPageChange={(event, newPage) => handleChangePage(newPage)}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}

        {listToDelete && (
          <DeleteListDialog
            open={!!listToDelete}
            onClose={(deleted) => {
              if (deleted) {
                const listId = listToDelete.id;
                const updated = displayedSearches.filter(
                  (search) => search.id !== listId
                );
                if (listType === "configListType") {
                  setConfigCount(configCount - 1);
                } else {
                  setPersonalCount(personalCount - 1);
                }
                setDisplayedSearches(updated);
                setSelected(selected.filter((row) => row.id !== listId));
              }
              setListToDelete(null);
            }}
            listId={listToDelete?.id}
            listType={isConfig ? ListType.Config : ListType.Personal}
            successMessage={translate("removedFromSavedSearches")}
            errorMessage={translate("removedFromSavedSearchesError")}
          ></DeleteListDialog>
        )}

        {listToRename && (
          <RenameListDialog
            name={listToRename?.name}
            id={listToRename?.id}
            type={isConfig ? ListType.Config : ListType.Personal}
            title={translate("renameSavedSearchDialogTitle")}
            successMessage={translate("renamedSavedSearchSuccess")}
            errorMessage={translate("renamedSavedSearchError")}
            open={!!listToRename}
            onClose={() => setListToRename(null)}
            onSave={(newListName) => {
              const updated = displayedSearches.map((search) =>
                search.id === listToRename.id
                  ? {
                      ...search,
                      name: newListName,
                    }
                  : search
              );
              setDisplayedSearches(updated);
              setListToRename(null);
            }}
          ></RenameListDialog>
        )}

        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={batchDeleteError}
          onClose={() => setBatchDeleteError(false)}
        >
          <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
            {translate("removedFromSavedSearchesError")}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

// TODO row selection is going to be a repeated feature, make it more DRY
